import type { DestroyRef, WritableSignal } from '@angular/core';
import { afterNextRender, inject, Injectable, Injector, runInInjectionContext, signal, untracked } from '@angular/core';
import type { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { createBlockForm, type BlockForm } from '@sites/entity-signal-form';

import type { NormalizedBlockEntity } from '@web/types';

import { HistoryFacade } from '@web/store/history/history.facade';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';

type BlockFormEntry = {
    form: BlockForm;
    entitySignal: WritableSignal<NormalizedBlockEntity>;
    storeSub: Subscription;
    refCount: number;
};

/**
 * The block fields a form edit can touch. `props` is the settings panel's whole
 * subject; the two envelope keys are here because they *are* edited from it —
 * `blockDisplay` on the display-condition sub-panel, `displayId` when a block is
 * detached from the library block it came from. Identity, the section list and
 * the shared-block flags stay the store's.
 */
const EDITABLE_KEYS = ['props', 'meta'] as const satisfies readonly (keyof NormalizedBlockEntity)[];

const pickEditable = (block: NormalizedBlockEntity | undefined): Partial<NormalizedBlockEntity> => {
    if (!block) return {};

    const changes: Record<string, unknown> = {};
    for (const key of EDITABLE_KEYS) {
        if (key in block) {
            changes[key] = (block as Record<string, unknown>)[key];
        }
    }

    return changes as Partial<NormalizedBlockEntity>;
};

/**
 * Ref-counted registry of block signal forms, the sibling of
 * {@link WebSectionFormService} / {@link WebColumnFormService}. The form drives
 * the persisted block JSON directly (v0 — see the `block` entity in
 * `@sites/entity-core`), so nothing here goes through the legacy bag bridge.
 */
@Injectable({ providedIn: 'root' })
export class WebBlockFormService {
    private readonly injector = inject(Injector);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly historyFacade = inject(HistoryFacade);

    private readonly registry = new Map<string, BlockFormEntry>();

    public getOrCreate(blockId: string): BlockForm {
        const existing = this.registry.get(blockId);
        if (existing) {
            existing.refCount++;
            return existing.form;
        }

        const entitySignal = signal<NormalizedBlockEntity>(undefined as any);

        // NgRx store.select() emits synchronously, so entitySignal is populated immediately
        const storeSub = this.blocksFacade
            .blockById$(blockId)
            .pipe(filter(Boolean))
            .subscribe((value) => entitySignal.set(value as NormalizedBlockEntity));

        const initialEntity = entitySignal();

        const form = runInInjectionContext(this.injector, () =>
            createBlockForm({
                entity: entitySignal,
                // The dispatcher picks the subtype factory off the entity signal,
                // which the store has already populated synchronously.
                onUpdate: (value: NormalizedBlockEntity) => {
                    const changes = pickEditable(value);

                    // A batch entry, not a change of its own: the v2 collector keys
                    // entries by entity, keeps the first `before` and the latest
                    // `after`, and flushes them together — so a slider drag is one
                    // undo step, and an edit that touches the block and its rows at
                    // once is undone as one thing.
                    this.historyFacade.addBatchUpdateToHistory(
                        'block',
                        { id: value.id, changes: pickEditable(untracked(entitySignal)) },
                        { id: value.id, changes }
                    );

                    this.blocksFacade.updateBlockProperties(value.id, changes as never);
                },
            })
        );

        // Sync immediately with the store value to avoid an empty model before the effect fires
        if (initialEntity) {
            form.syncFromEntity(structuredClone(initialEntity) as never);
        }

        this.registry.set(blockId, { form, entitySignal, storeSub, refCount: 1 });
        return form;
    }

    /**
     * Writes through the form for a caller that has no lifetime of its own — an
     * NgRx effect, say — and lets the reference go once the write has been
     * dispatched.
     *
     * The release cannot be inline: the form dispatches from an Angular `effect`,
     * so dropping the last reference on the next statement would destroy it before
     * the write ever left. `afterNextRender` runs after effects have flushed. When
     * a panel is already holding the form — the usual case, since the modal that
     * triggers this was opened from one — the ref-count never reaches zero anyway.
     */
    public writeThrough(blockId: string, apply: (form: BlockForm) => void): void {
        const form = this.getOrCreate(blockId);

        apply(form);

        afterNextRender(() => this.release(blockId), { injector: this.injector });
    }

    public getOrCreateScoped(blockId: string, destroyRef: DestroyRef): BlockForm {
        const form = this.getOrCreate(blockId);
        destroyRef.onDestroy(() => this.release(blockId));
        return form;
    }

    public getForm(blockId: string): BlockForm | null {
        return this.registry.get(blockId)?.form ?? null;
    }

    public release(blockId: string): void {
        const entry = this.registry.get(blockId);
        if (!entry) {
            return;
        }

        entry.refCount--;
        if (entry.refCount <= 0) {
            this.deleteForm(blockId);
        }
    }

    public deleteForm(blockId: string): void {
        const entry = this.registry.get(blockId);
        if (!entry) {
            return;
        }

        entry.storeSub.unsubscribe();
        entry.form.destroy();
        this.registry.delete(blockId);
    }
}
